import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  useMediaStore,
  type PendingJob,
} from '@brightmls/nextgen.lae.mediacenter';
import { useMediaSubscription } from '../../../hooks/useMediaSubscription';
import { useMediaJobs } from '../../../contexts/MediaJobsContext';
import { useService } from '../../../contexts/ServiceContext';
import { useMediaJobHandlers } from './context/useMediaJobHandlers';
import { useMediaV2Save } from './context/useMediaV2Save';
import { useMediaLogger } from './context/useMediaLogger';

/**
 * Always-mounted orchestrator that keeps media hydration, WebSocket
 * subscriptions, save handler, and job routing alive across route
 * changes between EditContent and MediaCenter.
 *
 * Lives in LoadedContent (EditRouteLoader) — persists for the entire
 * edit session of a listing.
 */
export const MediaJobOrchestrator: React.FC = () => {
  const { listingKey } = useParams<{ listingKey: string }>();
  const hydrate = useMediaStore((state) => state.hydrate);
  const hydratePendingJobs = useMediaStore((state) => state.hydratePendingJobs);
  const reset = useMediaStore((state) => state.reset);
  const isHydrated = useMediaStore((state) => state.isHydrated);
  const logger = useMediaLogger('MediaJobOrchestrator');

  const { initialMediaJobs, initialMedia } = useMediaJobs();
  const hasHydratedJobsRef = useRef(false);

  // Job handlers (updates MediaStore + syncs to FLC on completion)
  const { handleJobMessage } = useMediaJobHandlers();

  // Save handler (persists even when MediaCenter is unmounted)
  const { registerMediaV2SaveHandler } = useService();
  const { getDirtyMediaForSave } = useMediaV2Save();

  // Reset store on unmount (handles listing changes since tree remounts per listing)
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Register save handler (persistent across route changes)
  useEffect(() => {
    logger.log('Registering Media V2 save handler (orchestrator)');
    registerMediaV2SaveHandler?.(getDirtyMediaForSave);

    return () => {
      registerMediaV2SaveHandler?.(() => []);
    };
  }, [registerMediaV2SaveHandler, getDirtyMediaForSave, logger]);

  // 1. Hydrate MediaStore from initial listing data
  useEffect(() => {
    if (!isHydrated) {
      logger.log('Hydrating MediaStore with initial media');
      hydrate(initialMedia);
    }
  }, [initialMedia, hydrate, isHydrated, logger]);

  // 2. Hydrate pending jobs from get-listing response
  useEffect(() => {
    if (
      isHydrated &&
      initialMediaJobs.length > 0 &&
      !hasHydratedJobsRef.current
    ) {
      hasHydratedJobsRef.current = true;

      logger.log(`Hydrating ${initialMediaJobs.length} initial media jobs`);

      // Note: MediaJob and PendingJob have minor type mismatches (category: string vs union,
      // createdAt: string vs number) that are harmless at runtime since values match.
      const pendingJobs: PendingJob[] = initialMediaJobs.map(
        (job) =>
          ({
            jobId: job.jobId,
            fileName: job.fileName,
            fileSize: job.fileSize,
            category: job.category,
            status: job.status,
            aiStatus: job.aiStatus,
            failedStep: job.failedStep,
            previewUrl: job.previewUrl,
            processedUrl: job.processedUrl,
            resultUrl: job.resultUrl,
            error: job.error,
            displayOrder: job.mediaDisplayOrder,
            itemNumber: job.mediaItemNumber,
            imageMetadata: job.imageMetadata,
            pdfMetadata: job.pdfMetadata,
            createdAt: job.createdAt,
            updatedAt: job.updatedAt,
          }) as any,
      );

      hydratePendingJobs(pendingJobs);
    }
  }, [isHydrated, initialMediaJobs, hydratePendingJobs, logger]);

  // 3. Subscribe to WebSocket media updates (persists across route changes)
  // Wait for hydration before subscribing to avoid updates for items not yet in store
  useMediaSubscription({
    listingKey: listingKey || '',
    enabled: !!listingKey && isHydrated,
    reconcile: true,
    onJobUpdate: handleJobMessage,
    onError: (error) => {
      logger.error('Subscription error:', error);
    },
  });

  return null; // Renderless — exists purely for side effects
};
